import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { ClockCountdown } from "components/icons/ClockCountdown";
import Pencil from "components/icons/pencil";
import { YYYY_MM_DD } from "constants/date";
import { timezones } from "data/timezones";
import { CHAT_SETTING_BANNER_TYPE_DICT } from "dictionaries";
import { addHolidaySchema } from "formValidations/addHolidaySchema";
import { timedBannerSchema } from "formValidations/timedBannerSchema";
import { Field, FieldProps, Form, Formik } from "formik";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  useAddHolidayMutation,
  useCreateBannerMutation,
  useDeleteBannerMutation,
  useEditBannerMutation,
  useGetChatSettingsQuery,
} from "store/apis/chatSettings";
import { BackendError } from "types/utils/error";
import { formatDate } from "utils/formatDate";
import { AvailabilitySettings } from "components/AvailabilitySettings/AvailabilitySettings";
import { SmallInfo } from "components/icons/SmallInfo";
import ToolTip from "components/design/toolTip";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { HOLIDAY, SLA, TIMED, WEEKEND } from "constants/chatSettingBannerTypes";
import { TrashSimple } from "components/icons/TrashSimple";
import { EditPencil } from "components/icons/EditPencil";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { TableUI } from "components/design/TableUI";
import { Banner } from "components/DesignSystem/Banner/Banner";

export const ChatSettings = () => {
  const { data: chatSettings } = useGetChatSettingsQuery();
  const [
    editBanner,
    { isLoading: isEditingBanner, originalArgs: editBannerArgs },
  ] = useEditBannerMutation();
  const [addHoliday, { isLoading: isAddingHoliday }] = useAddHolidayMutation();
  const [createBanner, { isLoading: isCreatingBanner }] =
    useCreateBannerMutation();
  const [deleteBanner, { isLoading: isDeletingBanner }] =
    useDeleteBannerMutation();

  const TIMED_BANNER_ADD = "add";
  const TIMED_BANNER_EDIT = "edit";

  const { successToast, alertToast } = useToast();
  const addHolidayModal = useModal();
  const deleteTimedBannerModal = useModal();
  const [editBannerId, setEditBannerId] = useState<string | null>("");
  const [timedBannerMode, setTimedBannerMode] = useState<string | null>(null);
  const selectedEditBanner = chatSettings?.banners.find(
    (banner) => banner.uuid === editBannerId
  );

  const timedBanner = chatSettings?.banners.find(
    (b) => b.banner_type === TIMED
  );
  const timedBannerInitialValues = {
    banner_text:
      timedBannerMode === TIMED_BANNER_EDIT
        ? timedBanner?.banner_text ?? ""
        : "",
    expiry_date:
      timedBannerMode === TIMED_BANNER_EDIT
        ? timedBanner?.expiry_date ?? ""
        : "",
  };

  const BANNER_ORDER = [HOLIDAY, WEEKEND, SLA, TIMED];
  const sortedBanners = chatSettings?.banners
    .slice()
    .sort(
      (a, b) =>
        BANNER_ORDER.indexOf(a.banner_type) -
        BANNER_ORDER.indexOf(b.banner_type)
    );
  const hasTimedBanner = chatSettings?.banners.some(
    (b) => b.banner_type === TIMED
  );

  const createColumn = createColumnHelper<{ date: string; timezone: string }>();

  const columns = useMemo(
    () => [
      createColumn.accessor("date", {
        header: "DATE",
        cell: (info) => formatDate(info.getValue()),
        size: 75,
      }),
      createColumn.accessor("timezone", {
        header: "TIME ZONE",
        size: 25,
      }),
    ],
    []
  );

  const data = useMemo(
    () =>
      chatSettings?.holidays.map((holiday) => ({
        ...holiday,
        timezone:
          timezones.find((tz) => tz.value === holiday.timezone)?.abbr ||
          holiday.timezone,
      })) || [],
    [chatSettings]
  );

  const table = useReactTable({
    columns,
    data: data,
    getCoreRowModel: getCoreRowModel(),
  });

  const onBannerSave = async ({
    text,
    bannerId,
  }: {
    text: string;
    bannerId: string;
  }) => {
    try {
      await editBanner({
        uuid: bannerId,
        text,
      }).unwrap();
      setEditBannerId(null);
      successToast({ message: "Banner updated successfully" });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const onAddHoliday = async ({
    date,
    timezone,
  }: {
    date: string;
    timezone: string;
  }) => {
    try {
      await addHoliday({
        date,
        timezone,
      }).unwrap();
      successToast({ message: "Holiday added successfully" });
      addHolidayModal.close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const onDeleteTimedBanner = async () => {
    if (!timedBanner?.uuid) return;
    try {
      await deleteBanner({ banner_id: timedBanner.uuid }).unwrap();
      successToast({ message: "Timed banner deleted" });
      deleteTimedBannerModal.close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const onSubmitTimedBanner = async ({
    banner_text,
    expiry_date,
  }: {
    banner_text: string;
    expiry_date: string;
  }) => {
    try {
      if (timedBannerMode === TIMED_BANNER_EDIT && timedBanner?.uuid) {
        await editBanner({
          uuid: timedBanner.uuid,
          text: banner_text,
          expiry_date: expiry_date,
        }).unwrap();
        setEditBannerId(null);
        successToast({ message: "Edits saved successfully" });
        setTimedBannerMode(null);
      } else {
        await createBanner({
          banner_text,
          banner_type: "TIMED",
          expiry_date,
        }).unwrap();
        successToast({ message: "Timed banner is now live" });
        setTimedBannerMode(null);
      }
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <DashboardContainer className="t-h-full">
      <DashboardContainer.Content className="t-px-10 t-my-5">
        <div className="t-w-[88%] t-flex t-flex-col t-gap-8">
          <div className="t-flex t-flex-col t-gap-3 t-w-4/6">
            <p className="t-m-0 t-text-subtitle-sm">Banners</p>
            <div className="t-flex t-gap-3 t-flex-col">
              {sortedBanners?.map((banner) => (
                <div className="t-flex t-flex-col t-gap-1.5" key={banner.uuid}>
                  <p className="t-m-0 t-text-subtext-sm t-flex t-items-center t-gap-2">
                    {CHAT_SETTING_BANNER_TYPE_DICT[banner.banner_type]}
                    {banner.is_active && (
                      <Tag size="small" tagType="red" rounded>
                        Now Showing
                      </Tag>
                    )}
                    {banner.banner_type === TIMED && !banner.is_active && (
                      <Tag size="small" tagType="gray" rounded>
                        Expired
                      </Tag>
                    )}
                  </p>
                  <div className="t-py-2 t-px-3 t-flex t-gap-2 t-items-center t-bg-surface-lighter-grey t-text-neutral-40">
                    <ClockCountdown className="t-shrink-0" />
                    <span className="t-text-neutral t-text-body-sm">
                      {banner.banner_text}
                    </span>
                    <div className="t-ml-auto t-text-text-30 t-flex t-items-center t-gap-0.5">
                      <Button
                        size="xsmall"
                        customType="ghost_icon"
                        onClick={() =>
                          banner.banner_type === TIMED
                            ? setTimedBannerMode(TIMED_BANNER_EDIT)
                            : setEditBannerId(banner.uuid)
                        }
                      >
                        <EditPencil className="t-size-4 t-text-text-30" />
                      </Button>
                      {banner.banner_type === TIMED && (
                        <Button
                          type="button"
                          size="xsmall"
                          customType="ghost_icon"
                          onClick={deleteTimedBannerModal.open}
                        >
                          <TrashSimple className="t-size-4 t-text-text-30 t-p-0.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {banner.expiry_date && (
                    <span className="t-text-body-sm-italic t-italic t-text-text-30 t-font-light">
                      This banner{" "}
                      {banner.is_active ? "expires on" : "expired on"}{" "}
                      <span className="t-font-semibold">
                        {formatDate(banner.expiry_date)}
                      </span>{" "}
                      at 11:59 pm IST.
                    </span>
                  )}
                </div>
              ))}
              {!hasTimedBanner && (
                <Button
                  type="button"
                  customType="primary_outlined"
                  size="xsmall"
                  className="t-self-start"
                  onClick={() => setTimedBannerMode(TIMED_BANNER_ADD)}
                >
                  Set a timed banner
                </Button>
              )}
            </div>
          </div>
          {selectedEditBanner && (
            <Modal.Root
              onOpenChange={() => setEditBannerId(null)}
              open={Boolean(selectedEditBanner)}
            >
              <Modal.Content>
                <Formik
                  initialValues={{
                    text: selectedEditBanner.banner_text,
                    bannerId: selectedEditBanner.uuid,
                  }}
                  onSubmit={onBannerSave}
                >
                  {({ submitForm }) => (
                    <>
                      <Modal.Header>
                        <Modal.Title>
                          Edit{" "}
                          {
                            CHAT_SETTING_BANNER_TYPE_DICT[
                              selectedEditBanner.banner_type
                            ]
                          }
                        </Modal.Title>
                        <Modal.Close />
                      </Modal.Header>
                      <Modal.Body>
                        <Form className="t-m-0 t-flex t-flex-col t-gap-1">
                          <TextArea name="text" />
                        </Form>
                      </Modal.Body>
                      <Modal.FooterButtonGroup>
                        <Button
                          size="small"
                          customType="primary"
                          disabled={
                            isEditingBanner &&
                            editBannerArgs?.uuid === selectedEditBanner.uuid
                          }
                          isLoading={
                            isEditingBanner &&
                            editBannerArgs?.uuid === selectedEditBanner.uuid
                          }
                          onClick={submitForm}
                        >
                          Save
                        </Button>
                      </Modal.FooterButtonGroup>
                    </>
                  )}
                </Formik>
              </Modal.Content>
            </Modal.Root>
          )}

          <hr className="t-text-neutral-0" />

          <AvailabilitySettings />

          <hr className="t-text-neutral-0" />

          <div className="t-w-4/6">
            <div className="t-flex t-justify-between t-py-2">
              <p className="t-m-0 t-text-subtitle-sm">Holidays</p>
              <Modal.Root
                onOpenChange={addHolidayModal.toggle}
                open={addHolidayModal.isOpen}
              >
                <Modal.Trigger asChild>
                  <Button size="small" customType="primary_outlined">
                    Add
                  </Button>
                </Modal.Trigger>
                <Modal.Content useCustomOverlay>
                  <Formik
                    onSubmit={onAddHoliday}
                    initialValues={{
                      date: "",
                      timezone: "India Standard Time",
                    }}
                    validationSchema={addHolidaySchema}
                  >
                    {({ values, submitForm }) => {
                      const selectedTimezone = timezones.find(
                        (z) => z.value === values.timezone
                      );
                      return (
                        <>
                          <Modal.Header>
                            <Modal.Title>Add holiday</Modal.Title>
                            <Modal.Close />
                          </Modal.Header>
                          <Modal.Body>
                            <Form className="t-m-0 t-flex t-flex-col t-gap-4">
                              <Field name="date">
                                {({ field }: FieldProps) => {
                                  return (
                                    <>
                                      <DateInput
                                        {...field}
                                        required
                                        saveFormat={YYYY_MM_DD}
                                        portalId="date-of-holiday"
                                        label="Date of Holiday"
                                      />
                                    </>
                                  );
                                }}
                              </Field>
                              <Combobox
                                isClearable={false}
                                label="Select time zone"
                                menuPortalTarget={document.body}
                                withForm
                                name="timezone"
                                value={
                                  selectedTimezone
                                    ? {
                                        value: selectedTimezone?.value,
                                        label: `${selectedTimezone?.value} (${selectedTimezone?.abbr})`,
                                      }
                                    : null
                                }
                                options={timezones
                                  .map(({ value, abbr, text }) => [
                                    value,
                                    `${value} (${abbr})`,
                                  ])
                                  .sort((a, b) => a[0].localeCompare(b[0]))
                                  ?.map((option) => {
                                    return {
                                      label: option[1],
                                      value: option[0],
                                    };
                                  })}
                              />
                            </Form>
                          </Modal.Body>
                          <Modal.FooterButtonGroup>
                            <Modal.RawClose asChild>
                              <Button>Cancel</Button>
                            </Modal.RawClose>

                            <Button
                              onClick={submitForm}
                              customType="primary"
                              isLoading={isAddingHoliday}
                              disabled={isAddingHoliday}
                            >
                              Save
                            </Button>
                          </Modal.FooterButtonGroup>
                        </>
                      );
                    }}
                  </Formik>
                </Modal.Content>
              </Modal.Root>
            </div>
            <TableUI table={table} sticky={false} />

            <div className="t-mt-6">
              <Banner
                type="info"
                description="Saturday and Sunday are holidays"
              />
            </div>
          </div>

          <Modal.Root
            open={Boolean(timedBannerMode)}
            onOpenChange={() => setTimedBannerMode(null)}
          >
            <Modal.Content useCustomOverlay>
              <Formik
                onSubmit={onSubmitTimedBanner}
                initialValues={timedBannerInitialValues}
                validationSchema={timedBannerSchema}
                enableReinitialize
              >
                {({ values, submitForm, isValid, dirty }) => (
                  <>
                    <Modal.Header>
                      <Modal.Title>
                        {timedBannerMode === TIMED_BANNER_EDIT
                          ? "Edit Timed Banner"
                          : "Set Timed Banner"}
                      </Modal.Title>
                      <Modal.Close />
                    </Modal.Header>
                    <Modal.Body>
                      <Form className="t-m-0 t-flex t-flex-col t-gap-4">
                        <TextArea
                          name="banner_text"
                          label={
                            <span className="t-text-text-60">
                              Banner message
                            </span>
                          }
                          placeholder="Write the message to display in the banner"
                        />

                        <Field name="expiry_date">
                          {({ field }: FieldProps) => {
                            return (
                              <>
                                <div className="t-text-body-sm t-flex t-items-center t-gap-x-1 t-text-text-60">
                                  Banner last visible date
                                  <ToolTip
                                    side="right"
                                    text={
                                      <div className="t-text-left">
                                        The banner will remain visible until
                                        11:59 PM IST of the selected date, after
                                        which it will revert to the default
                                        banner.
                                      </div>
                                    }
                                  >
                                    <SmallInfo />
                                  </ToolTip>
                                </div>
                                <div className="-t-translate-y-3">
                                  <DateInput
                                    {...field}
                                    required
                                    saveFormat={YYYY_MM_DD}
                                    portalId="date-of-holiday"
                                    minDate={new Date()}
                                  />
                                </div>
                              </>
                            );
                          }}
                        </Field>
                      </Form>
                    </Modal.Body>
                    <Modal.FooterButtonGroup>
                      <Modal.RawClose asChild>
                        <Button>Cancel</Button>
                      </Modal.RawClose>

                      <Button
                        onClick={submitForm}
                        customType="primary"
                        isLoading={isCreatingBanner}
                        disabled={
                          isCreatingBanner ||
                          !isValid ||
                          (timedBannerMode === TIMED_BANNER_EDIT && !dirty)
                        }
                      >
                        {timedBannerMode === TIMED_BANNER_ADD
                          ? "Save"
                          : "Update"}
                      </Button>
                    </Modal.FooterButtonGroup>
                  </>
                )}
              </Formik>
            </Modal.Content>
          </Modal.Root>

          <Modal.Root
            open={deleteTimedBannerModal.isOpen}
            onOpenChange={deleteTimedBannerModal.close}
          >
            <Modal.Content>
              <StatusModal
                type="warning"
                title="Delete timed banner?"
                showCloseButton={false}
              >
                <div className="t-text-body-sm t-text-text-30">
                  If the banner is currently live, the banner will stop showing
                  right away and revert to the default banner.
                </div>
              </StatusModal>
              <Modal.Footer className="t-flex t-justify-end t-gap-3">
                <Button
                  type="button"
                  customType="secondary"
                  onClick={deleteTimedBannerModal.close}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  customType="primary"
                  onClick={onDeleteTimedBanner}
                  isLoading={isDeletingBanner}
                  disabled={isDeletingBanner}
                >
                  Delete banner
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Root>
        </div>
      </DashboardContainer.Content>
    </DashboardContainer>
  );
};
